import { useEffect, useState, useCallback } from "react";
import {
  KeyRound, FlaskConical, Rocket, CheckCircle2, Loader2, RefreshCw,
  RotateCcw, AlertTriangle,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import InlineConfirm from "./InlineConfirm";

/**
 * Brief 3.4.5 cutover-gated batch — Transaction milestones panel.
 *
 * Surfaces three idempotent event milestones on the merchant detail page:
 *   • credentials_issued (auto-fires from the Issue Credentials flow)
 *   • first_sandbox_txn  (admin button — manually mark first sandbox txn)
 *   • first_prod_txn     (admin button — manually mark first prod txn)
 *
 * Each entry shows: name + status pill + fired_at timestamp + Mark button
 * (when unfired) OR Re-fire override (super_admin escape hatch when fired
 * without a Telegram handle bound — useful when handle was added late).
 */
export default function TransactionMilestonesPanel({ merchantId, status }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [firingKey, setFiringKey] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/merchants/${merchantId}/event-milestones`,
      );
      setData(data);
      setError("");
    } catch (e) {
      setError(
        e?.response?.data?.detail?.message
          || e?.response?.data?.detail
          || "Could not load transaction milestones.",
      );
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Only render for approved/live merchants — milestone fires are no-ops
  // for everyone else and the panel would be visual noise on drafts.
  if (!["approved", "live"].includes(status)) return null;
  // Only super_admins see this — milestone enqueue is super_admin gated.
  if (!isSuperAdmin) return null;

  const onMark = async (milestone, label) => {
    if (firingKey) return;
    setFiringKey(milestone);
    try {
      const path = milestone === "first_sandbox_txn"
        ? "mark-first-sandbox-txn" : "mark-first-prod-txn";
      const { data } = await api.post(
        `/admin/merchants/${merchantId}/${path}`,
      );
      if (data?.queued) {
        toast.success(`${label} milestone queued for send.`);
      } else if (data?.skipped === "no_binding") {
        toast.warning(
          `${label} marked, but no Telegram handle is bound. `
          + `Add a handle, then use Re-fire.`,
        );
      } else if (data?.skipped === "already_fired") {
        toast.info(`${label} already fired earlier.`);
      } else {
        toast.success(`${label} marked.`);
      }
      refresh();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Could not mark ${label}.`,
      );
    } finally {
      setFiringKey(null);
    }
  };

  const onClear = async (milestone, label) => {
    if (firingKey) return;
    setFiringKey(milestone);
    try {
      await api.post(
        `/admin/merchants/${merchantId}/event-milestones/${milestone}/clear`,
      );
      toast.success(`${label} sentinel cleared.`);
      refresh();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Could not clear ${label} sentinel.`,
      );
    } finally {
      setFiringKey(null);
    }
  };

  return (
    <section
      data-testid="txn-milestones-section"
      className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6"
    >
      <header className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2.5">
          <Rocket className="h-4 w-4 text-emerald-300/80" />
          <h2 className="text-[15px] tracking-wide text-zinc-100">Transaction milestones</h2>
        </div>
        <button
          type="button"
          onClick={refresh}
          data-testid="txn-milestones-refresh"
          className="inline-flex items-center gap-1.5 text-[11.5px] text-zinc-400 hover:text-white transition-colors"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      {loading && !data && (
        <div className="text-[12px] text-zinc-500" data-testid="txn-milestones-loading">
          Loading…
        </div>
      )}
      {error && (
        <div data-testid="txn-milestones-error" className="rounded-xl border border-rose-400/20 bg-rose-400/[0.03] p-4 text-rose-200 text-[12.5px]">
          {error}
        </div>
      )}

      {data && (
        <>
          {!data.telegram_username_bound && (
            <div className="mb-3 rounded-md border border-amber-400/20 bg-amber-400/[0.04] p-3 text-[12px] text-amber-200 flex items-center gap-2">
              <AlertTriangle className="h-3 w-3" />
              No Telegram handle bound — marking a milestone will stamp the
              sentinel but the merchant won't receive a Telegram message.
            </div>
          )}
          <div className="space-y-2.5">
            <MilestoneRow
              icon={KeyRound}
              label="Credentials issued"
              shortLabel="credentials issuance"
              milestone="credentials_issued"
              state={data.milestones.credentials_issued}
              fireable={false}
              hint="Fires automatically when an admin issues API credentials."
              firingKey={firingKey}
              onClear={onClear}
            />
            <MilestoneRow
              icon={FlaskConical}
              label="First sandbox transaction"
              shortLabel="sandbox transaction"
              milestone="first_sandbox_txn"
              state={data.milestones.first_sandbox_txn}
              fireable
              firingKey={firingKey}
              onMark={onMark}
              onClear={onClear}
            />
            <MilestoneRow
              icon={Rocket}
              label="First production transaction"
              shortLabel="production transaction"
              milestone="first_prod_txn"
              state={data.milestones.first_prod_txn}
              fireable
              firingKey={firingKey}
              onMark={onMark}
              onClear={onClear}
            />
          </div>
        </>
      )}
    </section>
  );
}

function MilestoneRow({
  icon: Icon, label, shortLabel, milestone, state, fireable,
  hint, firingKey, onMark, onClear,
}) {
  const fired = state?.fired;
  const isBusy = firingKey === milestone;

  return (
    <div
      data-testid={`txn-milestone-row-${milestone}`}
      className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4 flex flex-wrap items-center justify-between gap-3"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-zinc-400" />
          <div className="text-[13px] text-zinc-100">{label}</div>
          {fired && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-400/[0.08] px-2 py-0.5 text-[10.5px] text-emerald-100"
              data-testid={`txn-milestone-pill-${milestone}`}
            >
              <CheckCircle2 className="h-3 w-3" /> Fired
            </span>
          )}
        </div>
        <div className="mt-1 text-[11.5px] text-zinc-500">
          {fired ? (
            <span>Fired {fmtTs(state.fired_at)}</span>
          ) : (
            <span>{hint || "Not yet fired."}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        {!fired && fireable && (
          <InlineConfirm
            message={`Mark first ${shortLabel}?`}
            onConfirm={() => onMark(milestone, shortLabel)}
            testid={`txn-milestone-mark-${milestone}`}
            trigger={({ onClick }) => (
              <button
                type="button"
                onClick={onClick}
                disabled={isBusy}
                data-testid={`txn-milestone-mark-${milestone}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-400/30 bg-emerald-400/[0.08] px-3 py-1.5 text-[11.5px] text-emerald-100 hover:bg-emerald-400/[0.14] disabled:opacity-50"
              >
                {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                Mark first {shortLabel}
              </button>
            )}
          />
        )}
        {fired && (
          <InlineConfirm
            message="Clear sentinel?"
            onConfirm={() => onClear(milestone, shortLabel)}
            testid={`txn-milestone-clear-${milestone}`}
            tone="danger"
            trigger={({ onClick }) => (
              <button
                type="button"
                onClick={onClick}
                disabled={isBusy}
                data-testid={`txn-milestone-clear-${milestone}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.10] px-3 py-1.5 text-[11px] text-zinc-300 hover:bg-white/[0.04] disabled:opacity-50"
              >
                {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                Re-fire
              </button>
            )}
          />
        )}
      </div>
    </div>
  );
}

function fmtTs(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}
